import { yupResolver } from '@hookform/resolvers/yup';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AuthError } from 'types';
import { validatePhoneNumber } from 'utils';
import { Input, PhoneInput } from 'components';
import { ArrowRightIcon } from 'components/Icon';

import styles from './Authentication.module.scss';

export type LoginProps = {
  onSubmitted?(): void;
  onOpenSignUp?(): void;
  onUserNotConfirmed?(username: string): void;
};

export type FormValues = {
  phoneNumber: string;
  password: string;
};

const validationSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .test('isValid', 'Phone number is invalid', value => !!value && validatePhoneNumber(value)),
  password: yup.string().required('Password is required')
});

const Login = ({ onSubmitted, onOpenSignUp, onUserNotConfirmed }: LoginProps) => {
  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      phoneNumber: '',
      password: ''
    }
  });

  const handleOpenSignUp = () => onOpenSignUp?.();

  const handleOnSubmit = async (validValues: FormValues) => {
    const { phoneNumber, password } = validValues;
    const username = phoneNumber.slice(1);

    try {
      await Auth.signIn(username, password);
      onSubmitted?.();
    } catch (e) {
      if (e instanceof Object && 'code' in e && 'message' in e) {
        const parsedError = e as AuthError;

        switch (parsedError.code) {
          case 'UserNotConfirmedException': {
            await Auth.resendSignUp(username);
            onUserNotConfirmed?.(username);
            break;
          }

          default: {
            form.setError('password', {
              message: parsedError.message,
              type: 'server'
            });
          }
        }
      } else if (e instanceof Error) {
        form.setError('password', { message: e.message, type: 'server' });
      }
    }
  };

  return (
    <div className={styles.login}>
      <form onSubmit={form.handleSubmit(handleOnSubmit)}>
        <div className={styles.fields}>
          <Controller
            control={form.control}
            name="phoneNumber"
            render={({ field, fieldState }) => (
              <PhoneInput
                className={styles.phone}
                invalid={!!fieldState.error?.message}
                feedback={fieldState.error?.message}
                placeholder="Your phone number"
                value={field.value}
                onChange={val => form.setValue('phoneNumber', val)}
              />
            )}
          />

          <Controller
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <Input
                type="password"
                className={styles.password}
                invalid={!!fieldState.error?.message}
                feedback={fieldState.error?.message}
                placeholder="Password"
                {...field}
              />
            )}
          />
        </div>

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className={cx({ [styles.active]: form.formState.isSubmitted })}
        >
          <span>Next</span>
          <ArrowRightIcon />
        </button>
      </form>

      <div className={styles.footer}>
        <span className={styles.mention}>Don't have an account?</span>
        <button type="button" className={styles.action} onClick={handleOpenSignUp}>
          Sign up
        </button>
      </div>
    </div>
  );
};

export default Login;
